import axios, { AxiosInstance, AxiosError, AxiosResponse } from 'axios';
import { env } from '@/core/config/env';
import { authenticateSocket } from '@/core/realtime/socket';
import i18n from '@/core/i18n/i18n';

/**
 * Standard API response format
 */
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: any;
    warning?: string;
  };
  requestId?: string;
}

/**
 * Create axios instance with dynamic baseURL
 * The baseURL will be resolved at runtime when requests are made
 */
const axiosInstance: AxiosInstance = axios.create({
  baseURL: env.apiUrl, // This uses the getter which evaluates window.location.origin at runtime
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Ensure baseURL is always current by updating it on each request
// Uses env.apiUrl which handles production (api.nxt48.com) vs development (localhost/ngrok)
axiosInstance.interceptors.request.use((config) => {
  config.baseURL = env.apiUrl;
  return config;
});

// Request interceptor - attach token, locale, and client ID
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Send the user's selected language to the backend
    // This overrides browser's Accept-Language for consistent locale handling
    const currentLang = i18n.language?.split('-')[0] || 'es';
    config.headers['Accept-Language'] = currentLang;
    
    // Send anonymous client ID for session tracking
    // This allows the backend to associate anonymous users with their preferences
    const clientId = localStorage.getItem('anon_client_id');
    if (clientId) {
      config.headers['x-client-id'] = clientId;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - map to standard format and handle refresh token
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    // Map backend response to standard format
    // Backend returns: { success, data, requestId }
    // We return the data directly, but keep the structure for consistency
    return response;
  },
  async (error: AxiosError) => {
    // No hacer refresh token ni redirigir en endpoints públicos o de autenticación
    // Estos endpoints deben manejar sus propios errores (login, register, etc.)
    const publicEndpoints = [
      '/auth/login', '/auth/register', '/auth/refresh', '/auth/wallet', '/auth/methods',
      '/meta-tags/active', '/config/public', '/system/health',
      '/bots/default', '/public' // Bot public endpoints
    ];
    const isAuthEndpoint = error.config?.url && publicEndpoints.some(endpoint => error.config!.url!.includes(endpoint));
    
    // Only handle 401 errors - network errors should NOT trigger logout
    // This prevents losing the session during backend restarts/hot reloads
    if (error.response?.status === 401 && !isAuthEndpoint) {
      // Try to refresh token
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        try {
          const response = await api.post('/auth/refresh', {
            refreshToken,
          });
          const { accessToken } = response.data.data;
          localStorage.setItem('accessToken', accessToken);
          
          // Authenticate socket connection with new token
          authenticateSocket(accessToken);
          
          // Retry original request
          if (error.config) {
            error.config.headers.Authorization = `Bearer ${accessToken}`;
            return axiosInstance.request(error.config);
          }
        } catch (refreshError: any) {
          // Only logout if refresh failed due to auth error (401)
          // Network errors should NOT clear the session
          const isNetworkError = !refreshError.response;
          if (isNetworkError) {
            console.warn('[API] Refresh failed due to network error, keeping session');
            // Don't redirect, let the original error propagate
          } else {
            // Actual auth failure - clear session
            console.log('[API] Refresh token invalid, clearing session');
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            window.location.href = '/login';
          }
        }
      } else {
        // No refresh token and got 401 - session is invalid
        window.location.href = '/login';
      }
    }

    // Map error to standard format
    // Extract the actual error details from backend response (not the whole response)
    const backendError = (error.response?.data as any)?.error;
    const apiError: ApiResponse = {
      success: false,
      error: {
        code: backendError?.code || error.response?.status?.toString() || 'UNKNOWN_ERROR',
        message: backendError?.message || error.message || 'An error occurred',
        details: backendError?.details,
        warning: backendError?.warning,
      },
      requestId: (error.response?.data as any)?.requestId,
    };

    return Promise.reject(apiError);
  }
);

/**
 * API client with standard response format
 * All methods return ApiResponse<T> format
 */
export const api = {
  get: <T = any>(url: string, config?: any): Promise<AxiosResponse<ApiResponse<T>>> => {
    return axiosInstance.get<ApiResponse<T>>(url, config);
  },
  post: <T = any>(url: string, data?: any, config?: any): Promise<AxiosResponse<ApiResponse<T>>> => {
    return axiosInstance.post<ApiResponse<T>>(url, data, config);
  },
  put: <T = any>(url: string, data?: any, config?: any): Promise<AxiosResponse<ApiResponse<T>>> => {
    return axiosInstance.put<ApiResponse<T>>(url, data, config);
  },
  patch: <T = any>(url: string, data?: any, config?: any): Promise<AxiosResponse<ApiResponse<T>>> => {
    return axiosInstance.patch<ApiResponse<T>>(url, data, config);
  },
  delete: <T = any>(url: string, config?: any): Promise<AxiosResponse<ApiResponse<T>>> => {
    return axiosInstance.delete<ApiResponse<T>>(url, config);
  },
};

export default api;
